import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Bell, LogOut, Moon, Menu, Sun, X, ChevronDown, Building2, Shield } from 'lucide-react';
import SystemStatusIndicator from './SystemStatusIndicator';
import VersionBadge from './VersionBadge';
import NotificationCenter from '../Platform/NotificationCenter';
import ProfileSelectorModal from './ProfileSelectorModal';
import LanguageSwitcher from './LanguageSwitcher';
import MfaSettingsModal from './MfaSettingsModal';

// Helper to format user category for display
const formatCategory = (category) => {
  if (!category) return 'User';
  const categoryMap = {
    'platform_admin': 'Platform Admin',
    'platform_user': 'Platform User',
    'platform': 'Platform User',
    'customer': 'Customer',
    'funder': 'Funder',
    'admin': 'Admin',
    'risk': 'Risk Officer'
  };
  return categoryMap[category.toLowerCase()] || category;
};

// Helper to get category badge color (compact, subtle style)
const getCategoryColor = (category, darkMode) => {
  if (!category) return darkMode ? 'bg-gray-500/10 text-gray-400' : 'bg-gray-500/10 text-gray-600';
  const cat = category.toLowerCase();
  if (cat.includes('platform') || cat === 'admin') return darkMode ? 'bg-purple-500/15 text-purple-300' : 'bg-purple-500/12 text-purple-600';
  if (cat === 'customer') return darkMode ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-500/12 text-blue-600';
  if (cat === 'funder') return darkMode ? 'bg-green-500/15 text-green-300' : 'bg-green-500/12 text-green-600';
  if (cat === 'risk') return darkMode ? 'bg-red-500/15 text-red-300' : 'bg-red-500/12 text-red-600';
  return darkMode ? 'bg-gray-500/10 text-gray-400' : 'bg-gray-500/10 text-gray-600';
};

const GlassTopBar = ({
  logoSrc,
  brandName,
  brandSubtitle = '',
  displayRole = '',
  displayName = '',
  userEmail = '',
  userCategory = '',
  darkMode,
  toggleDarkMode,
  isSidebarCollapsed,
  onSidebarToggle,
  onBrandClick = () => { },
  notifications = 0,
  onNotificationsClick = () => { },
  onLogout,
  showStatus = true,
  // White-label props
  currentProfile = null,
  availableProfiles = [],
  onProfileChange = null,
  // Brand color - default yellow for HaliAgent, can be overridden
  primaryColor = '#F6B800'
}) => {
  // Determine brand color based on brandName or primaryColor prop
  const getBrandColor = () => {
    if (brandName === 'HaliAgent') return '#F6B800'; // Yellow for HaliAgent
    if (brandName === 'HaliTrade') return '#dc2626'; // Red for HaliTrade
    if (brandName === 'HaliLab') return '#3b82f6'; // Blue for HaliLab
    if (brandName === 'HaliFund') return '#10B981'; // Green for HaliFund
    return primaryColor || '#F6B800';
  };
  const brandColor = getBrandColor();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);

  return (
    <>
      {/* Profile Selector Modal */}
      <ProfileSelectorModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentProfile={currentProfile}
        availableProfiles={availableProfiles}
        onProfileChange={onProfileChange}
        darkMode={darkMode}
        primaryColor={currentProfile?.primaryColor}
      />
      {/* MFA Settings Modal */}
      <MfaSettingsModal
        isOpen={isMfaModalOpen}
        onClose={() => setIsMfaModalOpen(false)}
        darkMode={darkMode}
      />
      <header
        className="glass-surface border-b sticky top-0 z-40 text-gray-900 dark:text-gray-100 border-black/10 dark:border-white/10"
      >
        <div className="px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4 min-w-0">
              <button
                onClick={onSidebarToggle}
                className="p-1.5 rounded-md transition-colors"
                title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {isSidebarCollapsed ? <Menu size={22} /> : <X size={22} />}
              </button>
              <button
                onClick={onBrandClick}
                className="flex items-center ml-1.5 gap-2 group shrink-0"
                aria-label={`Go to ${brandName} landing`}
              >
                <img
                  src={logoSrc}
                  alt={brandName}
                  className="h-9 w-9 object-contain transition-transform group-hover:scale-105"
                />
                <div className="text-left">
                  <div className="text-base font-extrabold tracking-tight">
                    <span className="text-gray-700 dark:text-gray-50">Hali</span>
                    <span style={{ color: brandColor }}>{(typeof brandName === 'string' ? brandName : '').replace('Hali', '')}</span>
                  </div>
                  <div className="text-[10px] font-medium" style={{ color: brandColor }}>
                    {brandSubtitle || 'powered by HaliSoft'}
                  </div>
                </div>
              </button>
              {showStatus && (
                <div className="hidden md:block shrink-0">
                  <SystemStatusIndicator inline darkMode={darkMode} />
                </div>
              )}
              <div className="shrink-0">
                <VersionBadge />
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* White-Label Profile Selector Button - compact */}
              {currentProfile && onProfileChange && (
                <button
                  onClick={() => setIsProfileModalOpen(true)}
                  className="flex items-center gap-1 px-1.5 py-1 rounded border transition-all bg-black/5 dark:bg-white/10 border-black/10 dark:border-white/20 hover:bg-black/10 dark:hover:bg-white/20"
                  title="Switch white-label profile"
                >
                  <Building2 size={14} />
                  {currentProfile?.companyName && (
                    <span className="text-[10px] font-semibold truncate max-w-[80px]">
                      {currentProfile.companyName}
                    </span>
                  )}
                  <ChevronDown size={12} />
                </button>
              )}

              {/* User Info Section - minimal single line */}
              {(displayName || displayRole || userEmail || userCategory) && (
                <div className="hidden sm:flex items-center gap-2 px-2 py-1 rounded border border-black/10 dark:border-white/20 bg-black/[0.02] dark:bg-white/5">
                  {displayName && (
                    <span className="text-[11px] font-medium truncate max-w-[80px]">{displayName}</span>
                  )}
                  <div className="flex items-center gap-1">
                    {userCategory && (
                      <span className={`px-1 py-0.5 rounded text-[9px] font-medium uppercase tracking-wide ${getCategoryColor(userCategory, darkMode)}`}>
                        {formatCategory(userCategory)}
                      </span>
                    )}
                    {displayRole && (
                      <span className={`px-1 py-0.5 rounded text-[9px] font-medium uppercase tracking-wide ${getCategoryColor(userCategory, darkMode)}`}>
                        {displayRole}
                      </span>
                    )}
                  </div>
                  {userEmail && (
                    <span className="text-[9px] truncate max-w-[120px] text-gray-500 dark:text-gray-400">{userEmail}</span>
                  )}
                </div>
              )}
              <LanguageSwitcher compact className="hidden sm:block" />
              <button
                onClick={toggleDarkMode}
                className="p-1.5 rounded-full transition-all border bg-black/5 dark:bg-white/10 border-black/10 dark:border-white/20 hover:bg-black/10 dark:hover:bg-white/20"
                aria-label="Toggle theme"
              >
                {darkMode ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button
                onClick={() => setIsMfaModalOpen(true)}
                className="p-1.5 rounded-full transition-all border bg-black/5 dark:bg-white/10 border-black/10 dark:border-white/20 hover:bg-black/10 dark:hover:bg-white/20"
                aria-label="Security settings"
                title="Security settings (2FA)"
              >
                <Shield size={16} />
              </button>
              <div className="text-current">
                <NotificationCenter />
              </div>
              {/* The visible copy is "Exit", but this is the ONLY logout control
                  in CustomerLayout (it does not pass onLogout to the sidebar, so
                  the sidebar's aria-label="Logout" footer button never renders).
                  Without an explicit accessible name, screen-reader and voice
                  users get "Exit" with no hint that it ends the session — and the
                  security e2e suite's getByRole('button', { name: /logout/i })
                  found nothing, silently skipped its click, and then failed the
                  "token cleared after logout" assertion. */}
              <button
                onClick={onLogout}
                aria-label="Logout"
                title="Logout"
                className="px-2.5 py-1.5 rounded-md flex items-center space-x-1.5 text-xs font-medium border bg-black/5 dark:bg-white/10 border-black/10 dark:border-white/20 hover:bg-black/10 dark:hover:bg-white/20"
              >
                <LogOut size={16} />
                <span>Exit</span>
              </button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

GlassTopBar.propTypes = {
  logoSrc: PropTypes.string.isRequired,
  brandName: PropTypes.string.isRequired,
  brandSubtitle: PropTypes.string,
  displayRole: PropTypes.string,
  displayName: PropTypes.string,
  userEmail: PropTypes.string,
  userCategory: PropTypes.string,
  darkMode: PropTypes.bool.isRequired,
  toggleDarkMode: PropTypes.func.isRequired,
  isSidebarCollapsed: PropTypes.bool.isRequired,
  onSidebarToggle: PropTypes.func.isRequired,
  onBrandClick: PropTypes.func,
  notifications: PropTypes.number,
  onNotificationsClick: PropTypes.func,
  onLogout: PropTypes.func.isRequired,
  showStatus: PropTypes.bool,
  borderColor: PropTypes.string,
  primaryColor: PropTypes.string,
  accentColor: PropTypes.string,
  currentProfile: PropTypes.object,
  availableProfiles: PropTypes.array,
  onProfileChange: PropTypes.func
};



export default GlassTopBar;
